import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { deleteAppointment, getAppointments, getProducts, updateAppointmentStatus } from '../lib/api';
import { Appointment, AppointmentStatus, Product } from '../types';
import { AppointmentDetailDrawer } from '../components/AppointmentDetailDrawer';
import { useConfirm, useToast } from '../ui/UIContext';
import { AdminCalendarSkeleton } from '../components/AdminSkeletons';

const STATUS_DOT: Record<AppointmentStatus, string> = {
  PENDING: 'bg-amber-400',
  CONFIRMED: 'bg-blue-500',
  COMPLETED: 'bg-green-500',
  CANCELLED: 'bg-gray-400',
};

const STATUS_BG: Record<AppointmentStatus, string> = {
  PENDING: 'bg-amber-50 border-amber-200 text-amber-800',
  CONFIRMED: 'bg-blue-50 border-blue-200 text-blue-800',
  COMPLETED: 'bg-green-50 border-green-200 text-green-800',
  CANCELLED: 'bg-gray-100 border-gray-200 text-gray-600',
};

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(monthStart: Date): Date[] {
  // Monday-first grid: 0=Mon..6=Sun
  const firstDow = (monthStart.getDay() + 6) % 7;
  const start = new Date(monthStart);
  start.setDate(monthStart.getDate() - firstDow);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export function AdminCalendarPage() {
  const { t, i18n } = useTranslation();
  const showToast = useToast();
  const confirm = useConfirm();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [appts, prods] = await Promise.all([getAppointments(), getProducts()]);
        setAppointments(appts);
        setProducts(prods);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const apptByDate = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    appointments.forEach((a) => {
      const list = map.get(a.appointmentDate) ?? [];
      list.push(a);
      map.set(a.appointmentDate, list);
    });
    map.forEach((list) =>
      list.sort((a, b) => a.timeSlot.localeCompare(b.timeSlot)),
    );
    return map;
  }, [appointments]);

  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);

  const monthLabel = useMemo(() => {
    return cursor.toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });
  }, [cursor, i18n.language]);

  const weekdayLabels = useMemo(() => {
    // Monday-first
    const ref = new Date(2024, 0, 1); // 2024-01-01 is a Monday
    const labels: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(ref);
      d.setDate(ref.getDate() + i);
      labels.push(
        d.toLocaleDateString(i18n.language, { weekday: 'short' }),
      );
    }
    return labels;
  }, [i18n.language]);

  const todayStr = ymd(new Date());

  const selected = selectedId ? appointments.find((a) => a.id === selectedId) ?? null : null;

  async function handleStatus(id: string, status: AppointmentStatus) {
    setBusyId(id);
    try {
      const updated = await updateAppointmentStatus(id, status);
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      showToast(t('admin.toast.statusUpdated'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: t('admin.confirmDelete.title'),
      message: t('admin.confirmDelete.message'),
      destructive: true,
      confirmLabel: t('admin.actions.delete'),
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await deleteAppointment(id);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      if (selectedId === id) setSelectedId(null);
      showToast(t('admin.toast.deleted'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete', 'error');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <AdminCalendarSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ink">{t('calendar.title')}</h1>
          <p className="text-ink-soft mt-1">{t('calendar.subtitle')}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            onClick={() => setCursor((c) => addMonths(c, -1))}
            className="p-2 rounded-md hover:bg-gray-100"
            aria-label={t('calendar.prev')}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{monthLabel}</h2>
            <button
              onClick={() => setCursor(startOfMonth(new Date()))}
              className="text-sm text-indigo-600 hover:underline"
            >
              {t('calendar.today')}
            </button>
          </div>
          <button
            onClick={() => setCursor((c) => addMonths(c, 1))}
            className="p-2 rounded-md hover:bg-gray-100"
            aria-label={t('calendar.next')}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {weekdayLabels.map((label) => (
            <div
              key={label}
              className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider text-center"
            >
              {label}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((day) => {
            const key = ymd(day);
            const inMonth = day.getMonth() === cursor.getMonth();
            const isToday = key === todayStr;
            const list = apptByDate.get(key) ?? [];
            return (
              <div
                key={key}
                className={`min-h-[110px] border-b border-e border-gray-200 p-2 ${
                  inMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-xs font-semibold ${
                      isToday
                        ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white'
                        : ''
                    }`}
                  >
                    {day.getDate()}
                  </span>
                  {list.length > 0 && (
                    <span className="text-[10px] text-gray-500">{list.length}</span>
                  )}
                </div>
                <div className="space-y-1">
                  {list.slice(0, 3).map((appt) => (
                    <button
                      key={appt.id}
                      onClick={() => setSelectedId(appt.id)}
                      className={`w-full text-start text-xs px-1.5 py-1 rounded border ${STATUS_BG[appt.status]} truncate flex items-center gap-1`}
                      title={`${appt.name} — ${productMap.get(appt.productId)?.title ?? ''}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[appt.status]}`} />
                      <span className="truncate">
                        {t(`form.timeSlots.${appt.timeSlot}`).slice(0, 3)} · {appt.name}
                      </span>
                    </button>
                  ))}
                  {list.length > 3 && (
                    <div className="text-[10px] text-gray-500 ps-1">
                      +{list.length - 3} {t('calendar.more')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-4 text-xs text-ink-soft">
        {(Object.keys(STATUS_DOT) as AppointmentStatus[]).map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[s]}`} />
            {t(`admin.status.${s.toLowerCase()}`)}
          </div>
        ))}
      </div>

      {selected && (
        <AppointmentDetailDrawer
          appointment={selected}
          product={productMap.get(selected.productId) ?? null}
          busy={busyId === selected.id}
          onClose={() => setSelectedId(null)}
          onStatusChange={(status) => handleStatus(selected.id, status)}
          onDelete={() => handleDelete(selected.id)}
          onUpdated={(updated) =>
            setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
          }
        />
      )}
    </section>
  );
}
